import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  CablePlanState,
  CablePlanResponse,
  CablePlan,
  FilterOptions,
} from "./types/cable-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: CablePlanState = {
  cablePlans: [],
  cablePlan: null,
  selectedCablePlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create DataPlan
export const createCablePlan = createAsyncThunk<
  CablePlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("cablePlans/create", async (dataPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/cable-plans`, dataPlanData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // "Content-Type": "application/json",
      },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data plan" },
    );
  }
});

// ✅ Fetch DataPlans (Paginated)
export const fetchCablePlans = createAsyncThunk<
  CablePlanResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("cablePlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 15,
      search,
      status,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (queryParams.category) params.category = queryParams.category;
      if (queryParams.sort_order) params.sort_order = queryParams.sort_order;

      res = await axios.get(`${API_URL}/cable-plans`, {
        params,
        headers,
      });
    }

    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plans" },
    );
  }
});

// ✅ Fetch Single DataPlan by ID
export const fetchCablePlanById = createAsyncThunk<
  CablePlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("cablePlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/cable-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plan" },
    );
  }
});

// ✅ Delete DataPlan
export const deleteCablePlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("cablePlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/cable-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data plan" },
    );
  }
});

// ✅ Update DataPlan
export const updateCablePlan = createAsyncThunk<
  CablePlan,
  { id: number; data: Partial<CablePlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("cablePlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/cable-plans/${id}?_method=PUT`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data plan" },
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("dataPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/cable-plans/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" },
    );
  }
});

// ✅ Slice
const cablePlanSlice = createSlice({
  name: "cablePlans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetCablePlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedCablePlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createCablePlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createCablePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.cablePlan) {
          state.cablePlans.unshift(action.payload.cablePlan);
        }
      })
      .addCase(createCablePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create data plan";
      })

      // Fetch All
      .addCase(fetchCablePlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCablePlans.fulfilled, (state, action) => {
        state.loading = false;
        state.cablePlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchCablePlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plans";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchCablePlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedCablePlan = null;
      })
      .addCase(fetchCablePlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCablePlan = action.payload;
      })
      .addCase(fetchCablePlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plan";
      })

      // Delete
      .addCase(deleteCablePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCablePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.cablePlans = state.cablePlans.filter(
          (plan) => plan.id !== action.payload,
        );
      })
      .addCase(deleteCablePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete data plan";
      })

      // Update
      .addCase(updateCablePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCablePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update data plan in list
        state.cablePlans = state.cablePlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan,
        );

        // Update selected data plan if it's the one updated
        if (state.selectedCablePlan?.id === action.payload.id) {
          state.selectedCablePlan = action.payload;
        }
      })
      .addCase(updateCablePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update data plan";
      });
  },
});

export const { setFilters, clearFilters, resetCablePlanState } =
  cablePlanSlice.actions;
export default cablePlanSlice.reducer;
